<script setup lang="ts">
type Props = {
  selectedReward: Reward;
};

const { selectedReward } = defineProps<Props>();

type Emits = {
  (e: 'close'): void;
};

const emit = defineEmits<Emits>();

const isAnimationComplete = ref(false);

const isCasinoGame = computed<boolean>(() => {
  return !!selectedReward.game_slug_url;
});

const isSportEvent = computed<boolean>(() => {
  return selectedReward.type === 'freebets';
});

const linkTo = computed<string>(() => {
  if (isCasinoGame.value) {
    return localeLink(`/games/${selectedReward.game_slug_url}`);
  }

  if (isSportEvent.value) {
    return localeLink(`/sports`);
  }

  return '';
});

onMounted(() => {
  setTimeout(() => {
    isAnimationComplete.value = true;
  }, 100);
});
</script>

<template>
  <div :class="$style.redeemSuccess">
    <div :class="$style.circleContainer">
      <svg
        :class="$style.circleSvg"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          :class="$style.circleBackground"
          cx="50"
          cy="50"
          r="45"
        />

        <circle
          :class="$style.circleProgress"
          cx="50"
          cy="50"
          r="45"
          :style="{ '--animation-delay': '100ms' }"
        />

        <g
          :class="$style.checkIcon"
          :style="{ '--animation-delay': '1200ms' }"
        >
          <path
            d="M30 50 L45 65 L70 35"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="4"
          />
        </g>
      </svg>
    </div>

    <div :class="$style.title">
      {{ $t('rewards.redeem_success.title') }}
    </div>

    <div :class="$style.buttons">
      <NuxtLink
        v-if="linkTo"
        :class="$style.playNowButton"
        :to="linkTo"
      >
        {{ $t('rewards.redeem_success.play_now') }}
      </NuxtLink>

      <button
        :class="$style.closeButton"
        @click="emit('close')"
      >
        {{ $t('rewards.redeem_success.button') }}
      </button>
    </div>
  </div>
</template>

<style module lang="postcss">
.redeemSuccess {
  @apply flex flex-col items-center justify-center text-center;

  .circleContainer {
    @apply relative mb-8 w-28 h-28;

    .circleSvg {
      @apply w-full h-full -rotate-90;

      .circleBackground {
        @apply fill-none stroke-white/10;
        stroke-width: 4;
      }

      .circleProgress {
        @apply fill-none stroke-success;
        stroke-width: 4;
        stroke-dasharray: 283;
        stroke-dashoffset: 283;
        animation: drawCircle 1s ease-in-out forwards;
        animation-delay: var(--animation-delay);
      }

      .checkIcon {
        @apply text-success absolute top-0 left-0 w-full h-full;
        opacity: 0;
        animation: fadeInCheck 300ms ease-in-out forwards;
        animation-delay: var(--animation-delay);
        transform: rotate(90deg);
        transform-origin: center;
      }
    }
  }

  .title {
    @apply text-2xl font-bold text-texts;
    animation: slideInUp 0.6s ease-out forwards;
    opacity: 0;
    transform: translateY(20px);
  }

  .buttons {
    @apply mt-4 flex items-center justify-center gap-3;

    .playNowButton {
      @apply bg-success text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 opacity-0;

      animation: slideInUp 0.6s ease-out forwards;
      transform: translateY(20px);

      &:hover {
        @apply bg-success/90;
      }
    }

    .closeButton {
      @apply bg-white/20 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 opacity-0;

      animation: slideInUp 0.6s ease-out forwards;
      transform: translateY(20px);

      &:hover {
        @apply bg-white/30;
      }
    }
  }
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes fadeInCheck {
  to {
    @apply opacity-100;
  }
}

@keyframes slideInUp {
  to {
    @apply opacity-100;

    transform: translateY(0);
  }
}
</style>
